import { PlaType, SubFormType } from '@ikegps/core-api';
import { IkeId } from '../../id';
import { plaTypeFromMetadata } from './form.pla.type';
import { FormModelMetadata } from './form.metadata';
import { FormModelState } from './form.state';
import { type FormFieldSubFormState, FormModelFieldSubForm, FormModelMutatorSubForm } from './subform/form.subform';

export class FormModelMutator extends FormModelMutatorSubForm {
	override displayName(displayName: string) {
		this.state.setDisplayName(displayName);
	}

	description(description: string) {
		this.state.setDescription(description);
	}
}
export function isSpidaCalcForm(form: FormModel): boolean {
	return form.plaType === PlaType.SPIDACALC;
}
export function isOCalcForm(form: FormModel): boolean {
	return form.plaType === PlaType.OCALC;
}

export class FormModel extends FormModelFieldSubForm {
	_state: FormModelState = new FormModelState(this);
	get state(): FormModelState {
		return (this._state = this._state || new FormModelState(this));
	}

	private _FMutator: FormModelMutator = new FormModelMutator(this);
	override get Mutator(): FormModelMutator {
		if (this._FMutator == null) {
			this._FMutator = new FormModelMutator(this);
		}
		return this._FMutator;
	}

	override get id(): string {
		return this.state.id;
	}
	override set id(v: string) {
		this.state.id = v;
	}
	get revision(): string {
		return this.state.revision;
	}
	set revision(v: string) {
		this.state.revision = v;
	}
	get version(): string {
		return this.state.version;
	}
	set version(v: string) {
		this.state.version = v;
	}
	get rootFormId(): string {
		return this.state.rootFormId;
	}
	set rootFormId(v: string) {
		this.state.rootFormId = v;
	}
	get name(): string {
		return this.state.name;
	}
	set name(v: string) {
		this.state.name = v;
	}
	get description(): string {
		return this.state.description;
	}
	set description(v: string) {
		this.state.description = v;
	}
	override get displayName(): string {
		return this.state.displayName;
	}
	override set displayName(v: string) {
		this.state.displayName = v;
	}
	get displayVersion(): number | null {
		return this.state.displayVersion;
	}
	set displayVersion(v: number) {
		this.state.displayVersion = v;
	}
	get updatedAt(): Date {
		return this.state.updatedAt;
	}
	set updatedAt(v: Date) {
		this.state.updatedAt = v;
	}
	get createdAt(): Date {
		return this.state.createdAt;
	}
	set createdAt(v: Date) {
		this.state.createdAt = v;
	}
	get updatedBy(): string {
		return this.state.updatedBy;
	}
	set updatedBy(v: string) {
		this.state.updatedBy = v;
	}
	get metadata(): FormModelMetadata {
		return this.state.metadata;
	}
	set metadata(v: FormModelMetadata) {
		this.state.metadata = v;
	}
	/** PLA (Pole Load Analysis) type derived from metadata, or null for a base (non-PLA) form. */
	get plaType(): PlaType | null {
		return plaTypeFromMetadata(this.metadata.options);
	}

	getTypedSubForm(subformType: SubFormType): FormModelFieldSubForm | null {
		const potentialField: FormModelFieldSubForm | null =
			this.fields.find(
				(field): field is FormModelFieldSubForm =>
					FormModelFieldSubForm.isSubForm(field) && field.subFormType === subformType
			) ?? null;
		return potentialField;
	}

	constructor() {
		super(null, null);
		this.revision = IkeId.generate();
		this.version = this.revision;
		this.rootFormId = this.revision;
		this.metadata = new FormModelMetadata(this);
	}

	override getForm() {
		return this;
	}

	override get stateValue(): FormFieldSubFormState {
		const fieldState = this.state.getFieldValue(this.fieldId) as FormFieldSubFormState | undefined;
		return {
			id: this.id,
			displayName: this.state.displayName,
			fields: fieldState?.fields ?? [],
			label: [],
			type: 'form',
			subFormType: fieldState?.subFormType,
		};
	}
}

export class FormModelEdit<T extends FormModelState> extends FormModel {
	formModel: FormModel;
	override _state: T;

	constructor(form: FormModel, state: T) {
		super();
		this.formModel = form;
		this.revision = form.revision;
		this._state = state;
	}
}
